// Client for the ppm-app worker (web builds only).
//
// Mirrors the contracts the rest of the app already expects:
// - reads come from an in-memory copy of the account's birds/settings in the
//   same shape DevStorage uses, so DatabaseService's non-Electron code paths
//   work unchanged;
// - license failures throw Error objects flagged isLicenseError with the same
//   codes the desktop main process uses.

const LICENSE_ERROR_CODES = new Set([
  'trial-bird-limit', 'trial-pdf-limit', 'readonly', 'unlicensed', 'test-pedigrees-paid'
]);

async function api(path, { method = 'GET', body } = {}) {
  const res = await fetch(path, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* non-JSON error body */ }
  if (!res.ok) {
    const error = new Error((data && data.error) || `Server error (${res.status})`);
    error.code = data && data.code;
    error.status = res.status;
    error.retryAfter = data && data.retryAfter;
    if (LICENSE_ERROR_CODES.has(error.code)) error.isLicenseError = true;
    if (res.status === 401) {
      error.unauthenticated = true;
      // Session died mid-use (expired cookie, password reset elsewhere) —
      // let the auth gate flip back to the sign-in screen.
      window.dispatchEvent(new Event('ppm-auth-lost'));
    }
    throw error;
  }
  return data;
}

// In-memory mirror of the account's data, DevStorage-shaped.
let cache = null;
let loadPromise = null;
// Bumped by resetCache so an in-flight /api/state response from BEFORE an
// auth change (sign-out, session loss) can never repopulate the cache with
// the previous account's data.
let generation = 0;
// License state piggybacked on the last auth response — consumed once by
// licenseState() so app boot doesn't fetch the same state twice.
let primedLicenseState = null;

class WebApi {
  // ---- data --------------------------------------------------------------

  static async getData() {
    if (cache) return cache;
    if (!loadPromise) {
      const gen = generation;
      loadPromise = api('/api/state').then((data) => {
        loadPromise = null;
        if (gen !== generation) return WebApi.getData(); // auth changed mid-flight
        cache = {
          birds: data.birds || [],
          settings: data.settings || {},
          raceResults: data.raceResults || [],
          charts: data.charts || [],
          chartRaces: data.chartRaces || [],
          chartBirds: data.chartBirds || []
        };
        return cache;
      }).catch((e) => { loadPromise = null; throw e; });
    }
    return loadPromise;
  }

  static resetCache() {
    cache = null;
    loadPromise = null;
    primedLicenseState = null;
    generation++;
  }

  static async createBird(birdData) {
    const { bird } = await api('/api/birds', { method: 'POST', body: birdData });
    const data = await this.getData();
    // A cold cache loads /api/state AFTER the POST, so the snapshot may
    // already contain the new bird — don't add it twice.
    if (!data.birds.some((b) => b.id === bird.id)) data.birds.push(bird);
    return bird;
  }

  static async updateBird(id, birdData) {
    const { bird } = await api(`/api/birds/${id}`, { method: 'PUT', body: birdData });
    const data = await this.getData();
    const idx = data.birds.findIndex((b) => b.id === bird.id);
    if (idx !== -1) data.birds[idx] = bird; else data.birds.push(bird);
    return bird;
  }

  static async deleteBird(id) {
    // Route params arrive as strings ("/birds/12" → "12"); cache rows hold
    // numbers. Normalize before comparing or the deleted bird haunts the
    // cache until the next full reload.
    const numId = Number(id);
    await api(`/api/birds/${numId}`, { method: 'DELETE' });
    const data = await this.getData();
    data.birds = data.birds
      .map((b) => ({
        ...b,
        sire_id: b.sire_id === numId ? null : b.sire_id,
        dam_id: b.dam_id === numId ? null : b.dam_id
      }))
      .filter((b) => b.id !== numId);
    // The server cascades the bird's results and chart rows; mirror that in
    // the cache.
    if (Array.isArray(data.raceResults)) {
      data.raceResults = data.raceResults.filter((r) => r.bird_id !== numId);
    }
    if (Array.isArray(data.chartBirds)) {
      data.chartBirds = data.chartBirds.filter((cb) => cb.bird_id !== numId);
    }
  }

  // ---- bird sharing ------------------------------------------------------

  // Share a bird (+ its pedigree) to another account by email. Server gates
  // this to paid users and rejects unknown recipient emails.
  static async shareBird(birdId, email) {
    return api('/api/shares', { method: 'POST', body: { bird_id: Number(birdId), email } });
  }

  // The recipient's "Shared with me" inbox (display metadata only).
  static async listIncomingShares() {
    return (await api('/api/shares/incoming')).shares || [];
  }

  // One shared package: { share, package } — the package is fed to importData.
  static async getShare(id) {
    return api(`/api/shares/${Number(id)}`);
  }

  // Dismiss a share (decline, or clean up after a successful import).
  static async deleteShare(id) {
    return api(`/api/shares/${Number(id)}`, { method: 'DELETE' });
  }

  // ---- race results ------------------------------------------------------

  static async addRaceResult(birdId, resultData) {
    const { result } = await api(`/api/birds/${Number(birdId)}/results`, { method: 'POST', body: resultData });
    const data = await this.getData();
    if (!Array.isArray(data.raceResults)) data.raceResults = [];
    if (!data.raceResults.some((r) => r.id === result.id)) data.raceResults.push(result);
    return result;
  }

  static async updateRaceResult(id, resultData) {
    const { result } = await api(`/api/results/${Number(id)}`, { method: 'PUT', body: resultData });
    const data = await this.getData();
    const idx = (data.raceResults || []).findIndex((r) => r.id === result.id);
    if (idx !== -1) data.raceResults[idx] = result; else (data.raceResults ||= []).push(result);
    return result;
  }

  static async deleteRaceResult(id) {
    const numId = Number(id);
    await api(`/api/results/${numId}`, { method: 'DELETE' });
    const data = await this.getData();
    if (Array.isArray(data.raceResults)) {
      data.raceResults = data.raceResults.filter((r) => r.id !== numId);
    }
  }

  // ---- season charts -----------------------------------------------------
  // Each mutation mirrors the server's cascade rules into the cached state,
  // matching what the next cold /api/state load would return.

  static async createChart(chartData) {
    const { chart } = await api('/api/charts', { method: 'POST', body: chartData });
    const data = await this.getData();
    if (!Array.isArray(data.charts)) data.charts = [];
    if (!data.charts.some((c) => c.id === chart.id)) data.charts.push(chart);
    return chart;
  }

  static async updateChart(id, chartData) {
    const { chart } = await api(`/api/charts/${Number(id)}`, { method: 'PUT', body: chartData });
    const data = await this.getData();
    const idx = (data.charts || []).findIndex((c) => c.id === chart.id);
    if (idx !== -1) data.charts[idx] = chart; else (data.charts ||= []).push(chart);
    return chart;
  }

  static async deleteChart(id) {
    const numId = Number(id);
    await api(`/api/charts/${numId}`, { method: 'DELETE' });
    const data = await this.getData();
    const raceIds = new Set((data.chartRaces || []).filter((r) => r.chart_id === numId).map((r) => r.id));
    data.charts = (data.charts || []).filter((c) => c.id !== numId);
    data.chartRaces = (data.chartRaces || []).filter((r) => r.chart_id !== numId);
    data.chartBirds = (data.chartBirds || []).filter((b) => b.chart_id !== numId);
    // The server unlinks (not deletes) the chart's results.
    data.raceResults = (data.raceResults || []).map((r) =>
      raceIds.has(r.chart_race_id) ? { ...r, chart_race_id: null } : r);
  }

  static async addChartRace(chartId, raceData) {
    const { race } = await api(`/api/charts/${Number(chartId)}/races`, { method: 'POST', body: raceData });
    const data = await this.getData();
    if (!Array.isArray(data.chartRaces)) data.chartRaces = [];
    if (!data.chartRaces.some((r) => r.id === race.id)) data.chartRaces.push(race);
    return race;
  }

  static async updateChartRace(id, raceData) {
    const { race } = await api(`/api/chart-races/${Number(id)}`, { method: 'PUT', body: raceData });
    const data = await this.getData();
    const idx = (data.chartRaces || []).findIndex((r) => r.id === race.id);
    if (idx !== -1) data.chartRaces[idx] = race; else (data.chartRaces ||= []).push(race);
    // The server cascades the rename into linked results' name/date copies.
    data.raceResults = (data.raceResults || []).map((r) =>
      r.chart_race_id === race.id ? { ...r, race: race.race, race_date: race.race_date } : r);
    return race;
  }

  static async deleteChartRace(id) {
    const numId = Number(id);
    await api(`/api/chart-races/${numId}`, { method: 'DELETE' });
    const data = await this.getData();
    data.chartRaces = (data.chartRaces || []).filter((r) => r.id !== numId);
    data.raceResults = (data.raceResults || []).map((r) =>
      r.chart_race_id === numId ? { ...r, chart_race_id: null } : r);
  }

  static async addChartBird(chartId, birdId) {
    const { chartBird } = await api(`/api/charts/${Number(chartId)}/birds`, {
      method: 'POST', body: { bird_id: Number(birdId) }
    });
    const data = await this.getData();
    if (!Array.isArray(data.chartBirds)) data.chartBirds = [];
    if (!data.chartBirds.some((b) => b.id === chartBird.id)) data.chartBirds.push(chartBird);
    return chartBird;
  }

  static async removeChartBird(id) {
    const numId = Number(id);
    await api(`/api/chart-birds/${numId}`, { method: 'DELETE' });
    const data = await this.getData();
    data.chartBirds = (data.chartBirds || []).filter((b) => b.id !== numId);
  }

  // ---- weekly diary -----------------------------------------------------

  static async getDiaryEntries(start, end) {
    const query = new URLSearchParams({ start, end });
    const data = await api(`/api/diary?${query.toString()}`);
    return data.entries || [];
  }

  static async getAllDiaryEntries() {
    const data = await api('/api/diary');
    return data.entries || [];
  }

  static async saveDiaryWeek(weekStart, entries) {
    return api('/api/diary/week', {
      method: 'PUT',
      body: { week_start: weekStart, entries }
    });
  }

  // The server echoes back what it actually stored, because it does not always
  // store what it was sent: an uploaded pedigree background goes to R2 and the
  // settings row keeps a /api/photos/ URL. Mirroring the echo (not the sent
  // value) keeps the cache honest until the next /api/state, and hands the
  // caller the URL it should render.
  static async setSetting(key, value) {
    const res = await api('/api/settings', { method: 'PUT', body: { key, value } });
    const data = await this.getData();
    const stored = res && Object.prototype.hasOwnProperty.call(res, 'value')
      ? res.value
      : (value === null || value === undefined ? null : String(value));
    data.settings[key] = stored;
    return stored;
  }

  static async exportDownload() {
    const res = await fetch('/api/export');
    if (res.status === 401) {
      window.dispatchEvent(new Event('ppm-auth-lost'));
      throw new Error('Please sign in again to export.');
    }
    if (!res.ok) {
      // Surface the server's message (e.g. the photo-budget explanation)
      // instead of a bare status code.
      let message = `Export failed (${res.status})`;
      try {
        const data = await res.json();
        if (data && data.error) message = data.error;
      } catch (e) { /* non-JSON body */ }
      throw new Error(message);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pedigree-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // ---- license -----------------------------------------------------------

  static async licenseState() {
    if (primedLicenseState) {
      const state = primedLicenseState;
      primedLicenseState = null;
      return state;
    }
    return (await api('/api/license/state')).state;
  }

  static async trialStart() {
    return api('/api/license/trial', { method: 'POST', body: {} });
  }

  static async activate(licenseKey) {
    return api('/api/license/activate', { method: 'POST', body: { license_key: licenseKey } });
  }

  static async refresh() {
    return api('/api/license/refresh', { method: 'POST', body: {} });
  }

  static async deactivate() {
    return api('/api/license/deactivate', { method: 'POST', body: {} });
  }

  static async cancelSubscription() {
    return api('/api/license/cancel', { method: 'POST', body: {} });
  }

  static async recordPdfExport(exportId, metadata = {}) {
    return api('/api/license/record-pdf', {
      method: 'POST',
      body: {
        ...(exportId ? { export_id: exportId } : {}),
        ...metadata
      }
    });
  }

  static async getPdfHistory(birdId = null) {
    const hasBirdFilter = birdId !== null && birdId !== undefined;
    const parsedBirdId = Number(birdId);
    if (hasBirdFilter && (!Number.isSafeInteger(parsedBirdId) || parsedBirdId <= 0)) {
      throw new Error('Invalid bird identifier');
    }
    const query = hasBirdFilter ? `?bird_id=${encodeURIComponent(parsedBirdId)}` : '';
    return (await api(`/api/pdf-history${query}`)).history || [];
  }

  static async buyUrl(plan) {
    return (await api(`/api/license/buy-url?plan=${encodeURIComponent(plan)}`)).url;
  }

  // ---- auth --------------------------------------------------------------

  static async me() {
    const r = await api('/api/auth/me');
    primedLicenseState = r.state || null;
    return r;
  }

  static async signup(email, password) {
    const r = await api('/api/auth/signup', { method: 'POST', body: { email, password } });
    primedLicenseState = r.state ? { ...r.state, verification: r.verification } : null;
    return r;
  }

  static async login(email, password) {
    const r = await api('/api/auth/login', { method: 'POST', body: { email, password } });
    primedLicenseState = r.state || null;
    return r;
  }

  static async logout() {
    WebApi.resetCache();
    return api('/api/auth/logout', { method: 'POST', body: {} });
  }

  static async requestReset(email) {
    return api('/api/auth/request-reset', { method: 'POST', body: { email } });
  }

  static async resendVerification() {
    return api('/api/auth/resend-verification', { method: 'POST', body: {} });
  }

  static async verifyEmail(token) {
    return api('/api/auth/verify-email', { method: 'POST', body: { token } });
  }

  static async resetPassword(token, password) {
    return api('/api/auth/reset', { method: 'POST', body: { token, password } });
  }
}

export default WebApi;
